import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import {
  ConfirmationModal,
  useModals,
  Modal,
  ModalProps,
  Button,
  Input,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface FormEditTitleDescriptionModalProps extends ModalProps {
  data: {
    title: string | null;
    description: string | null;
  };

  onSave: (title: string | null, description: string | null) => Promise<void>;
  onDelete: () => Promise<void>;
}

type FormEditTitleDescriptionModalForm = {
  title: string | null;
  description: string | null;
};

export const FormEditTitleDescriptionModal = ({
  data,
  onSave,
  onDelete,
  ...props
}: FormEditTitleDescriptionModalProps) => {
  const { close, openModal } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const schema = z.object({
    title: z.string().nullable(),
    description: z.string().nullable(),
  });

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormEditTitleDescriptionModalForm>({
    resolver: zodResolver(schema),
    values: {
      title: data.title,
      description: data.description,
    },
    mode: "onChange",
  });

  const onSubmit = handleSubmit(async (data) => {
    setIsSaving(true);
    await onSave(data.title, data.description);
    setIsSaving(false);
    close(id);
  });

  const handleDelete = () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        await onDelete();
        close(id);
      },
    });
  };

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("common:edit")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              className={"w-full"}
              type={"submit"}
              onClick={onSubmit}
              isLoading={isSaving}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Stack gap={"lg"}>
            <Input
              label={t("common:title")}
              type="text"
              {...register("title")}
              error={errors.title?.message}
            />
            <Stack>
              <Title
                header={t("common:description")}
                size={"xs"}
                paddingBottom={true}
                paddingTop={true}
              />
              <Controller
                control={control}
                name={"description"}
                render={({ field: { value, onChange } }) => (
                  <RichEditor value={value ?? ""} onChange={onChange} />
                )}
              />
            </Stack>
            <DeleteListItem
              title={t("surveys:delete_from_item_question")}
              onDelete={handleDelete}
            />
          </Stack>
        </form>
      </Modal>
    </>
  );
};
